import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import loadable from '@loadable/component';

import { types as sdkTypes } from '../../../util/sdkLoader';
import { parse } from '../../../util/urlHelpers';
import { propTypes } from '../../../util/types';
import { ensureListing } from '../../../util/data';
import {
  sdkBoundsToFixedCoordinates,
  hasSameSDKBounds,
} from '../../../util/maps';
import { createMapboxMap } from '../../../util/mapboxSupport';
import { FormattedMessage } from '../../../util/reactIntl';

import SearchMapPriceLabel from '../SearchMapPriceLabel/SearchMapPriceLabel';
// Lazy-load SearchMapInfoCard to avoid SSR issues with swiper ES module
const SearchMapInfoCard = loadable(() =>
  import(/* webpackChunkName: "SearchMapInfoCard" */ '../SearchMapInfoCard/SearchMapInfoCard')
);
import SearchMapGroupLabel from '../SearchMapGroupLabel/SearchMapGroupLabel';
import { groupedByCoordinates, reducedToArray } from './SearchMap.helpers';
import css from './SearchMapWithMapbox.module.css';
import CustomFullscreenControl from './CustomFullscreenControl';
import MobileCloseControl from './MobileCloseControl';

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';
export const SOURCE_AUTOCOMPLETE = 'autocomplete';
const BOUNDS_FIXED_PRECISION = 8;
const INFO_CARD_VERTICAL_OFFSET_PX = 12;

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

/**
 * Fit part of map (descriped with bounds) to visible map-viewport
 *
 * @param {Object} map - map that needs to be centered with given bounds
 * @param {SDK.LatLngBounds} bounds - the area that needs to be visible when map loads.
 */
export const fitMapToBounds = (map, bounds, options) => {
  const { padding = 0, isAutocompleteSearch = false } = options;

  // map bounds as string literal for google.maps
  const mapBounds = sdkBoundsToMapboxBounds(bounds);
  const paddingOptionMaybe = padding == null ? { padding } : {};
  const eventData = isAutocompleteSearch
    ? { searchSource: SOURCE_AUTOCOMPLETE }
    : {};

  // If bounds are given, use it (defaults to center & zoom).
  if (map && mapBounds) {
    map.fitBounds(mapBounds, {
      ...paddingOptionMaybe,
      linear: false,
      duration: 500,
      eventData,
    });
  }
};

/**
 * Convert Mapbox formatted LatLng object to Sharetribe SDK's LatLng coordinate format
 * Longitudes > 180 and < -180 are converted to the correct corresponding value
 * between -180 and 180.
 *
 * @param {LngLat} mapboxLngLat - Mapbox LngLat
 *
 * @return {SDKLatLng} - Converted latLng coordinate
 */
export const mapboxLngLatToSDKLatLng = lngLat => {
  const mapboxLng = lngLat.lng;

  // For bounding boxes that overlap the antimeridian Mapbox sometimes gives
  // longitude values outside -180 and 180 degrees.Those values are converted
  // so that longitude is always between -180 and 180.
  const lng =
    mapboxLng > 180
      ? mapboxLng - 360
      : mapboxLng < -180
        ? mapboxLng + 360
        : mapboxLng;

  return new SDKLatLng(lngLat.lat, lng);
};

/**
 * Convert Mapbox formatted bounds object to Sharetribe SDK's bounds format
 *
 * @param {LngLatBounds} mapboxBounds - Mapbox LngLatBounds
 *
 * @return {SDKLatLngBounds} - Converted bounds
 */
export const mapboxBoundsToSDKBounds = mapboxBounds => {
  if (!mapboxBounds) {
    return null;
  }

  const ne = mapboxBounds.getNorthEast();
  const sw = mapboxBounds.getSouthWest();
  return new SDKLatLngBounds(
    mapboxLngLatToSDKLatLng(ne),
    mapboxLngLatToSDKLatLng(sw)
  );
};

/**
 * Convert sdk bounds that overlap the antimeridian into values that can
 * be passed to Mapbox. This is achieved by converting the SW longitude into
 * a value less than -180 that flows over the antimeridian.
 *
 * @param {SDKLatLng} bounds - bounds passed to the map
 *
 * @return {LngLatBoundsLike} a bounding box that is compatible with Mapbox
 */
const sdkBoundsToMapboxBounds = bounds => {
  if (!bounds) {
    return null;
  }
  const { ne, sw } = bounds;

  // if sw lng is > ne lng => the bounds overlap antimeridian
  // => flip the nw lng to the negative side so that the value
  // is less than -180
  const swLng = sw.lng > ne.lng ? -360 + sw.lng : sw.lng;

  return [[swLng, sw.lat], [ne.lng, ne.lat]];
};

/**
 * Return map bounds as SDKBounds
 *
 * @param {Mapbox} map - Mapbox map from where the bounds are asked
 *
 * @return {SDKLatLngBounds} - Converted bounds of given map
 */
export const getMapBounds = map => mapboxBoundsToSDKBounds(map.getBounds());

/**
 * Return map center as SDKLatLng
 *
 * @param {Mapbox} map - Mapbox map from where the center is asked
 *
 * @return {SDKLatLng} - Converted center of given map
 */
export const getMapCenter = map => mapboxLngLatToSDKLatLng(map.getCenter());

/**
 * Check if map library is loaded
 */
export const isMapsLibLoaded = () =>
  typeof window !== 'undefined' && window.mapboxgl && window.mapboxgl;

/**
 * Return price labels grouped by listing locations.
 * This is a helper function for SearchMapWithMapbox component.
 */
const priceLabelsInLocations = (
  listings,
  activeListingId,
  infoCardOpen,
  clickedListings,
  onListingClicked,
  mapComponentRefreshToken
) => {
  const listingArraysInLocations = reducedToArray(
    groupedByCoordinates(listings)
  );
  const infoCardOpenIds = Array.isArray(infoCardOpen)
    ? infoCardOpen.map(l => l?.id?.uuid).filter(id => typeof id === 'string')
    : infoCardOpen?.id?.uuid
      ? [infoCardOpen.id.uuid]
      : [];
  // On mobile, use clickedListings to maintain pin active state while swiping
  const clickedListingsIds = Array.isArray(clickedListings)
    ? clickedListings.map(l => l?.id?.uuid).filter(id => typeof id === 'string')
    : clickedListings?.id?.uuid
      ? [clickedListings.id.uuid]
      : [];
  const priceLabels = listingArraysInLocations.reverse().map(listingArr => {
    const listingIds = listingArr
      .map(l => l?.id?.uuid)
      .filter(id => typeof id === 'string');
    const hasActiveListing =
      activeListingId && listingIds.includes(activeListingId.uuid);
    const hasOpenInfoCard = listingIds.some(id =>
      infoCardOpenIds.includes(id)
    );
    // On mobile, also check clickedListings to keep pin active while swiping
    const hasClickedListings = listingIds.some(id =>
      clickedListingsIds.includes(id)
    );
    const isActive = !!(hasActiveListing || hasOpenInfoCard || hasClickedListings);

    // If location contains only one listing, print price label
    if (listingArr.length === 1) {
      const listing = listingArr[0];

      // if the listing is open, don't print price label
      // if (infoCardOpen != null && infoCardOpenIds.includes(listing.id.uuid)) {
      //   return null;
      // }

      // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
      const { geolocation } = listing.attributes;

      const key = listing.id.uuid;
      return {
        markerId: `price_${key}`,
        location: geolocation,
        offset: [0, -INFO_CARD_VERTICAL_OFFSET_PX],
        type: 'price',
        componentProps: {
          key,
          isActive,
          isInfoCardSelection: hasOpenInfoCard,
          className: LABEL_HANDLE,
          listing,
          onListingClicked,
          mapComponentRefreshToken,
        },
      };
    }

    // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
    const firstListing = ensureListing(listingArr[0]);
    const geolocation = firstListing.attributes.geolocation;

    const key = listingArr[0].id.uuid;
    return {
      markerId: `group_${key}`,
      location: geolocation,
      type: 'group',
      componentProps: {
        key,
        isActive,
          isInfoCardSelection: hasOpenInfoCard,
        className: LABEL_HANDLE,
        listings: listingArr,
        onListingClicked,
        mapComponentRefreshToken,
      },
    };
  });
  return priceLabels;
};

/**
 * Return info card. This is a helper function for SearchMapWithMapbox component.
 */
const infoCardComponent = (
  infoCardOpen,
  onListingInfoCardClicked,
  createURLToListing,
  mapComponentRefreshToken
) => {
  // On mobile, don't show info card overlay - MapPreview handles it instead
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
  if (isMobile || !infoCardOpen) {
    return null;
  }

  const listingsArray = Array.isArray(infoCardOpen)
    ? infoCardOpen
    : [infoCardOpen];

  const firstListing = ensureListing(listingsArray[0]);
  const key = firstListing.id.uuid;
  const geolocation = firstListing.attributes.geolocation;

  return {
    markerId: `infoCard_${key}`,
    location: geolocation,
    componentProps: {
      key,
      mapComponentRefreshToken,
      className: INFO_CARD_HANDLE,
      listings: listingsArray,
      onListingInfoCardClicked,
      createURLToListing,
    },
  };
};

const extractInfoCardIds = infoCardOpen => {
  if (!infoCardOpen) {
    return [];
  }

  const listingsArray = Array.isArray(infoCardOpen)
    ? infoCardOpen
    : [infoCardOpen];

  return listingsArray
    .map(listing => listing?.id?.uuid)
    .filter(id => typeof id === 'string');
};

const hasInfoCardSelectionChanged = (prevInfoCard, nextInfoCard) => {
  const prevIds = extractInfoCardIds(prevInfoCard);
  const nextIds = extractInfoCardIds(nextInfoCard);

  if (prevIds.length !== nextIds.length) {
    return true;
  }

  return prevIds.some((id, index) => id !== nextIds[index]);
};

/**
 * SearchMap component using Mapbox as map provider
 */
class SearchMapWithMapbox extends Component {
  constructor(props) {
    super(props);
    this.map =
      typeof window !== 'undefined' && window.mapboxMap
        ? window.mapboxMap
        : null;
    this.currentMarkers = [];
    this.currentInfoCard = null;
    // isMapUnavailable: the browser cannot run Mapbox GL (no WebGL). The page keeps working;
    // only the map area shows a notice. See util/mapboxSupport.js.
    this.state = { mapContainer: null, isMapReady: false, isMapUnavailable: false };
    this.viewportBounds = null;
    this.isProgrammaticUpdate = false; // Track programmatic bounds updates
    this.isMapInitialized = false; // Track if map has been initialized
    this.hasUserInteracted = false; // Track if user has manually interacted with map
    this.ensureInfoCardTimeout = null;
    this.currentInfoCardOffsets = { x: 0, y: 0 };
    this.lastInfoCardKey = null; // Track last info card to detect new popups
    this.infoCardNeedsRepositioning = false; // Flag to track if repositioning is needed
    this.fullscreenControl = null; // Store reference to fullscreen control

    this.onMount = this.onMount.bind(this);
    this.onMoveend = this.onMoveend.bind(this);
    this.initializeMap = this.initializeMap.bind(this);
    this.handleDoubleClickOnInfoCard = this.handleDoubleClickOnInfoCard.bind(
      this
    );
    this.handleMobilePinchZoom = this.handleMobilePinchZoom.bind(this);
    this.ensureInfoCardInView = this.ensureInfoCardInView.bind(this);
    this.deferEnsureInfoCardInView = this.deferEnsureInfoCardInView.bind(this);
    this.isDesktopViewport = this.isDesktopViewport.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevProps.location, this.props.location)) {
      // If no mapSearch url parameter is given, this is original location search
      const { mapSearch } = parse(this.props.location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      if (!mapSearch) {
        this.viewportBounds = null;
      }
    }

    // Handle fullscreen state change - resize map when container dimensions change
    if (this.map && prevProps.isFullscreen !== this.props.isFullscreen) {
      // Wait for CSS transition to complete, then resize
      setTimeout(() => {
        if (this.map && this.map.getContainer()) {
          this.map.resize();
        }
      }, 300); // Match the CSS transition duration
    }

    // Update fullscreen control callback if prop changed
    if (this.fullscreenControl && prevProps.onToggleFullscreen !== this.props.onToggleFullscreen) {
      this.fullscreenControl._onToggleFullscreen = this.props.onToggleFullscreen || null;
    }

    if (this.map) {
      const currentBounds = getMapBounds(this.map);

      // Do not call fitMapToBounds if bounds are the same.
      // Our bounds are viewport bounds, and fitBounds will try to add margins around those bounds
      // that would result to zoom-loop (bound change -> fitmap -> bounds change -> ...)
      // Also skip if viewportBounds exists (user has interacted with map) to prevent conflicts
      // Only update if bounds are significantly different or viewportBounds is null (initial load)
      if (!isEqual(this.props.bounds, currentBounds) && !this.viewportBounds) {
        // Mark as programmatic update before calling fitMapToBounds
        this.isProgrammaticUpdate = true;
        fitMapToBounds(this.map, this.props.bounds, {
          padding: 20,
          isAutocompleteSearch: true,
        });
        // Clear the flag after fitBounds animation finishes (duration: 500ms)
        setTimeout(() => {
          this.isProgrammaticUpdate = false;
          this.isMapInitialized = true;
        }, 600);
      }
    }

    if (!this.map && this.state.mapContainer && !this.state.isMapUnavailable) {
      this.initializeMap();

      /* Notify parent component that Mapbox map is loaded */
      this.props.onMapLoad(this.map);
    } else if (
      prevProps.mapComponentRefreshToken !== this.props.mapComponentRefreshToken
    ) {
      /* Notify parent component that Mapbox map is loaded */
      this.props.onMapLoad(this.map);
    }

    const infoCardChanged = hasInfoCardSelectionChanged(
      prevProps.infoCardOpen,
      this.props.infoCardOpen
    );

    // Check if only activeListingId changed (from card hover) - don't reposition in this case
    const onlyActiveListingChanged = 
      prevProps.activeListingId !== this.props.activeListingId &&
      !infoCardChanged &&
      prevProps.infoCardOpen === this.props.infoCardOpen;

    // Force repositioning when map container size changes (e.g., fullscreen toggle, window resize)
    const mapContainerResized = 
      prevState.mapContainer && this.state.mapContainer &&
      (prevState.mapContainer.offsetWidth !== this.state.mapContainer.offsetWidth ||
       prevState.mapContainer.offsetHeight !== this.state.mapContainer.offsetHeight);

    // Only trigger repositioning if:
    // 1. Info card actually changed (new card opened)
    // 2. Map just became ready
    // 3. Fullscreen state changed
    // 4. Container was resized
    // NOT when only activeListingId changes (card hover)
    if (
      this.props.infoCardOpen &&
      !onlyActiveListingChanged &&
      (infoCardChanged ||
        (!prevState.isMapReady && this.state.isMapReady) ||
        prevProps.isFullscreen !== this.props.isFullscreen ||
        mapContainerResized)
    ) {
      // Force repositioning for new cards, map ready, fullscreen changes, or container resize
      if (infoCardChanged || !prevState.isMapReady || prevProps.isFullscreen !== this.props.isFullscreen || mapContainerResized) {
        this.infoCardNeedsRepositioning = true;
      }
      this.deferEnsureInfoCardInView();
    }
  }

  componentWillUnmount() {
    if (this.ensureInfoCardTimeout && typeof window !== 'undefined') {
      window.clearTimeout(this.ensureInfoCardTimeout);
      this.ensureInfoCardTimeout = null;
    }
    if (this.currentInfoCard && this.currentInfoCard.markerContainer) {
      this.currentInfoCard.markerContainer.removeEventListener(
        'dblclick',
        this.handleDoubleClickOnInfoCard
      );
    }
    document.removeEventListener(
      'gesturestart',
      this.handleMobilePinchZoom,
      false
    );
    document.removeEventListener(
      'gesturechange',
      this.handleMobilePinchZoom,
      false
    );
    document.removeEventListener(
      'gestureend',
      this.handleMobilePinchZoom,
      false
    );
  }

  onMount(element) {
    // This prevents pinch zoom to affect whole page on mobile Safari.
    document.addEventListener(
      'gesturestart',
      this.handleMobilePinchZoom,
      false
    );
    document.addEventListener(
      'gesturechange',
      this.handleMobilePinchZoom,
      false
    );
    document.addEventListener('gestureend', this.handleMobilePinchZoom, false);

    this.setState({ mapContainer: element });
  }

  onMoveend(e) {
    if (!this.map) {
      return;
    }

    const viewportMapBounds = getMapBounds(this.map);
    const viewportBounds = sdkBoundsToFixedCoordinates(
      viewportMapBounds,
      BOUNDS_FIXED_PRECISION
    );

    // Ignore moveend events triggered by programmatic bounds updates
    if (this.isProgrammaticUpdate) {
      this.viewportBounds = viewportBounds;
      return;
    }

    // Ignore events until map is initialized (prevents initial load events)
    if (!this.isMapInitialized) {
      this.viewportBounds = viewportBounds;
      this.isMapInitialized = true;
      return;
    }

    // If reusableMapHiddenHandle is given and parent element has that class,
    // we don't listen moveend events.
    // This fixes mobile Chrome bug that sends map events to invisible map components.
    const isHiddenByReusableMap =
      this.props.reusableMapHiddenHandle &&
      this.state.mapContainer.parentElement.classList.contains(
        this.props.reusableMapHiddenHandle
      );
    if (isHiddenByReusableMap) {
      this.viewportBounds = viewportBounds;
      return;
    }

    const viewportMapCenter = getMapCenter(this.map);

    // ViewportBounds from (previous) rendering differ from viewportBounds currently set to map
    // I.e. user has changed the map somehow: moved, panned, zoomed, resized
    const viewportBoundsChanged =
      this.viewportBounds &&
      !hasSameSDKBounds(this.viewportBounds, viewportBounds);

    // On the first user interaction, just mark that user has interacted
    // but don't trigger onMapMoveEnd (prevents unnecessary API calls on first interaction)
    if (!this.hasUserInteracted && viewportBoundsChanged) {
      this.hasUserInteracted = true;
      this.viewportBounds = viewportBounds;
      return;
    }

    // Only call onMapMoveEnd on subsequent user interactions (after first one)
    if (this.hasUserInteracted && viewportBoundsChanged) {
      this.props.onMapMoveEnd(viewportBoundsChanged, {
        viewportBounds,
        viewportMapCenter,
      });
      
      // If info card is open and map has moved, check if repositioning is needed
      // This ensures popup stays within bounds after map pan/zoom
      if (this.props.infoCardOpen && this.isDesktopViewport()) {
        this.infoCardNeedsRepositioning = true;
        this.deferEnsureInfoCardInView();
      }
    }
    this.viewportBounds = viewportBounds;
  }

  initializeMap() {
    const isReady =
      typeof window !== 'undefined' &&
      window.mapboxgl &&
      window.mapboxgl.accessToken;
    if (!isReady) {
      window.addEventListener('mapboxgl:ready', () => this.initializeMap(), { once: true });
      return;
    }

    const { offsetHeight, offsetWidth } = this.state.mapContainer;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;
    if (hasDimensions) {
      // Never throws. Bare `new mapboxgl.Map()` threw "Failed to initialize WebGL" in
      // browsers without a GPU, and from componentDidUpdate that reached the route error
      // boundary — the whole search page became "Something went wrong".
      const map = createMapboxMap({
        container: this.state.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v10',
        scrollZoom: true,
        interactive: true,
        touchZoomRotate: true,
        touchPitch: false,
        pitchWithRotate: false,
        doubleClickZoom: true,
        boxZoom: true,
        dragRotate: false,
        dragPan: true,
      });
      if (!map) {
        this.setState({ isMapUnavailable: true });
        if (this.props.onMapUnavailable) {
          this.props.onMapUnavailable();
        }
        return;
      }
      this.map = map;
      window.mapboxMap = this.map;

      // Geolocate control - added on both mobile and desktop
      const geolocate = new window.mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true, // keeps following the user if they move
        showUserHeading: true    // shows the device heading
      });
      this.map.addControl(geolocate, 'bottom-right');

      // Add zoom controls
      var nav = new window.mapboxgl.NavigationControl({ showCompass: false });
      this.map.addControl(nav, 'top-right');

      // Detect if mobile (using same breakpoint as Map.js: 1024px)
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;

      // Mobile: Add close control and zoom controls
      if (isMobile) {
        const mobileClose = new MobileCloseControl({
          onClose: this.props.onClose || null,
        });
        this.map.addControl(mobileClose, 'top-right');
      } else if (this.props.onToggleFullscreen) {
        // Desktop: Add fullscreen control only when the caller explicitly requests it
        this.fullscreenControl = new CustomFullscreenControl({
          onToggleFullscreen: this.props.onToggleFullscreen,
          container: this.state.mapContainer?.closest('[class*="rightContent"]') || null,
        });
        this.map.addControl(this.fullscreenControl, 'top-right');
      }

      this.map.on('moveend', this.onMoveend);

      // Introduce rerendering after map is ready (to include labels),
      // but keep the map out of state life cycle.
      this.setState({ isMapReady: true });
    }
  }

  handleMobilePinchZoom(e) {
    e.preventDefault();
    // A hack to prevent pinch zoom gesture in mobile Safari
    // Otherwise, pinch zoom would zoom both map and the document.
    document.body.style.zoom = 0.99;
  }

  isDesktopViewport() {
    return typeof window !== 'undefined' && window.innerWidth > 1024;
  }

  deferEnsureInfoCardInView(attempt = 0) {
    if (!this.isDesktopViewport() || !this.props.infoCardOpen) {
      return;
    }

    if (this.ensureInfoCardTimeout) {
      window.clearTimeout(this.ensureInfoCardTimeout);
    }

    this.ensureInfoCardTimeout = window.setTimeout(() => {
      this.ensureInfoCardInView(attempt);
    }, 80);
  }

  ensureInfoCardInView(attempt = 0) {
    if (this.ensureInfoCardTimeout && typeof window !== 'undefined') {
      window.clearTimeout(this.ensureInfoCardTimeout);
      this.ensureInfoCardTimeout = null;
    } else {
      this.ensureInfoCardTimeout = null;
    }

    if (!this.props.infoCardOpen) {
      this.currentInfoCardOffsets = { x: 0, y: 0 };
      this.lastInfoCardKey = null;
      this.infoCardNeedsRepositioning = false;
      return;
    }

    if (
      !this.map ||
      !this.isDesktopViewport() ||
      !this.currentInfoCard ||
      !this.state.mapContainer
    ) {
      if (attempt < 5) {
        this.deferEnsureInfoCardInView(attempt + 1);
      }
      return;
    }

    const cardContainer = this.currentInfoCard.markerContainer;
    const cardRoot = cardContainer?.firstChild;
    if (!cardRoot) {
      if (attempt < 5) {
        this.deferEnsureInfoCardInView(attempt + 1);
      }
      return;
    }

    const applyOffsets = (x, y, { force = false } = {}) => {
      if (
        !force &&
        this.currentInfoCardOffsets.x === x &&
        this.currentInfoCardOffsets.y === y
      ) {
        return;
      }

      cardRoot.style.setProperty('--infoCardDynamicTranslateX', `${x}px`);
      cardRoot.style.setProperty('--infoCardDynamicTranslateY', `${y}px`);
      this.currentInfoCardOffsets = { x, y };
    };

    // Get current rects with existing offsets applied
    const cardRect = cardRoot.getBoundingClientRect();
    const mapRect = this.state.mapContainer.getBoundingClientRect();
    const padding = 16;

    // Check if popup is currently within bounds with existing offsets
    const isCurrentlyInBounds =
      cardRect.left >= mapRect.left + padding &&
      cardRect.right <= mapRect.right - padding &&
      cardRect.top >= mapRect.top + padding &&
      cardRect.bottom <= mapRect.bottom - padding;

    // If popup is already correctly positioned and we don't need repositioning, don't recalculate
    // This prevents position shifts when:
    // - Mouse leaves map area
    // - activeListingId changes from card hover (only highlights pins, doesn't change info card)
    if (isCurrentlyInBounds && !this.infoCardNeedsRepositioning) {
      return;
    }

    // Additional check: if we have valid offsets and the card is the same, 
    // and we're not forced to reposition, skip recalculation
    // This prevents unnecessary recalculations when only activeListingId changes
    if (
      !this.infoCardNeedsRepositioning &&
      this.currentInfoCardOffsets.x !== 0 &&
      this.currentInfoCardOffsets.y !== 0 &&
      this.lastInfoCardKey === this.currentInfoCard.markerId
    ) {
      return;
    }

    // Reset to base position only when necessary (new popup or forced recalculation)
    // Store if this is a new info card to determine if we should reset
    const isNewInfoCard = !this.lastInfoCardKey || 
      this.lastInfoCardKey !== this.currentInfoCard.markerId;
    
    // Store previous offsets before resetting (for validation fallback)
    const previousOffsets = { ...this.currentInfoCardOffsets };
    
    if (isNewInfoCard) {
      this.lastInfoCardKey = this.currentInfoCard.markerId;
      this.infoCardNeedsRepositioning = true;
      applyOffsets(0, 0, { force: true });
    } else {
      // For existing popup, temporarily reset to calculate overflow accurately
      // but preserve current position if calculation fails
      applyOffsets(0, 0, { force: true });
    }

    // Recalculate rects after reset
    const resetCardRect = cardRoot.getBoundingClientRect();
    const resetMapRect = this.state.mapContainer.getBoundingClientRect();

    const overflowLeft = resetMapRect.left + padding - resetCardRect.left;
    const overflowRight = resetCardRect.right - (resetMapRect.right - padding);
    let offsetX = 0;
    if (overflowLeft > 0) {
      offsetX = overflowLeft;
    } else if (overflowRight > 0) {
      offsetX = -overflowRight;
    }

    const overflowTop = resetMapRect.top + padding - resetCardRect.top;
    const overflowBottom = resetCardRect.bottom - (resetMapRect.bottom - padding);
    let offsetY = 0;
    if (overflowTop > 0) {
      offsetY = overflowTop;
    } else if (overflowBottom > 0) {
      offsetY = -overflowBottom;
    }

    // Validate the calculated position is reasonable
    // If calculation seems off (e.g., popup would be way outside), keep previous position
    const calculatedPositionValid = 
      Math.abs(offsetX) < 1000 && Math.abs(offsetY) < 1000 &&
      resetMapRect.width > 0 && resetMapRect.height > 0;

    if (!calculatedPositionValid && !isNewInfoCard) {
      // Restore previous position if calculation seems invalid
      applyOffsets(previousOffsets.x, previousOffsets.y, { force: true });
      this.infoCardNeedsRepositioning = false;
      return;
    }

    if (!offsetX && !offsetY) {
      applyOffsets(0, 0);
      this.infoCardNeedsRepositioning = false;
      return;
    }

    applyOffsets(offsetX, offsetY);
    this.infoCardNeedsRepositioning = false;
  }

  handleDoubleClickOnInfoCard(e) {
    e.stopPropagation();
  }

  render() {
    const {
      id,
      className,
      listings,
      activeListingId,
      infoCardOpen,
      onListingClicked,
      onListingInfoCardClicked,
      createURLToListing,
      mapComponentRefreshToken,
      config,
      selectedCurrency,
      exchangeRates,
      onClose,
    } = this.props;

    if (this.map) {
      // Create markers out of price labels and grouped labels
      const labels = priceLabelsInLocations(
        listings,
        activeListingId,
        infoCardOpen,
        this.props.clickedListings,
        onListingClicked,
        mapComponentRefreshToken
      );

      // If map has moved or info card opened, unnecessary markers need to be removed
      const removableMarkers = differenceBy(
        this.currentMarkers,
        labels,
        'markerId'
      );
      removableMarkers.forEach(rm => rm.marker.remove());

      // Helper function to create markers to given container
      const createMarker = (data, markerContainer) => {
        const markerOptions = { anchor: 'bottom' };
        if (data && data.offset) {
          markerOptions.offset = data.offset;
        }
        return new window.mapboxgl.Marker(markerContainer, markerOptions)
          .setLngLat([data.location.lng, data.location.lat])
          .addTo(this.map);
      };

      // SearchMapPriceLabel and SearchMapGroupLabel:
      // create a new marker or use existing one if markerId is among previously rendered markers
      this.currentMarkers = labels
        .filter(v => v != null)
        .map(m => {
          const existingMarkerId = this.currentMarkers.findIndex(
            marker => m.markerId === marker.markerId && marker.marker
          );

          if (existingMarkerId >= 0) {
            const { marker, markerContainer, ...rest } = this.currentMarkers[
              existingMarkerId
            ];
            return { ...rest, ...m, markerContainer, marker };
          } else {
            const markerContainer = document.createElement('div');
            markerContainer.setAttribute('id', m.markerId);
            markerContainer.classList.add(css.labelContainer);
            const marker = createMarker(m, markerContainer);
            return { ...m, markerContainer, marker };
          }
        });

      /* Create marker for SearchMapInfoCard component */
      if (infoCardOpen) {
        const infoCard = infoCardComponent(
          infoCardOpen,
          onListingInfoCardClicked,
          createURLToListing,
          mapComponentRefreshToken
        );

        // Only recreate info card marker if it's a different card or doesn't exist
        // This prevents unnecessary recreation when only activeListingId changes
        const isNewInfoCard = !this.currentInfoCard || 
          this.currentInfoCard.markerId !== infoCard.markerId;

        if (isNewInfoCard) {
          // Remove old info card if it exists
          if (this.currentInfoCard) {
            this.currentInfoCard.markerContainer.removeEventListener(
              'dblclick',
              this.handleDoubleClickOnInfoCard
            );
            if (this.currentInfoCard.marker) {
              this.currentInfoCard.marker.remove();
            }
          }

          // marker container and its styles
          const infoCardContainer = document.createElement('div');
          infoCardContainer.setAttribute('id', infoCard.markerId);
          infoCardContainer.classList.add(css.infoCardContainer);
          infoCardContainer.addEventListener(
            'dblclick',
            this.handleDoubleClickOnInfoCard,
            false
          );

          this.currentInfoCard = {
            ...infoCard,
            markerContainer: infoCardContainer,
            marker: infoCard ? createMarker(infoCard, infoCardContainer) : null,
          };
        } else {
          // Update component props but keep the same marker and container
          // This preserves the position when only activeListingId changes
          this.currentInfoCard = {
            ...this.currentInfoCard,
            ...infoCard,
            markerContainer: this.currentInfoCard.markerContainer,
            marker: this.currentInfoCard.marker,
          };
        }
      } else {
        if (this.currentInfoCard) {
          this.currentInfoCard.markerContainer.removeEventListener(
            'dblclick',
            this.handleDoubleClickOnInfoCard
          );
          if (this.currentInfoCard.marker) {
            this.currentInfoCard.marker.remove();
          }
        }
        this.currentInfoCard = null;
      }
    }

    // The browser cannot run Mapbox GL. Say so in the map's place and leave the rest of the
    // page — results, search box, suggestions — exactly as it is. On mobile the map is a
    // full-screen layer whose only way out is a control attached to the map itself, so the
    // notice carries its own close button.
    const mapUnavailableNotice = this.state.isMapUnavailable ? (
      <div className={css.mapUnavailable} role="status">
        <p className={css.mapUnavailableText}>
          <FormattedMessage id="SearchMap.mapUnavailable" />
        </p>
        {onClose ? (
          <button type="button" className={css.mapUnavailableClose} onClick={onClose}>
            <FormattedMessage id="SearchMap.mapUnavailableClose" />
          </button>
        ) : null}
      </div>
    ) : null;

    return (
      <>
        <div
          id={id}
          ref={this.onMount}
          className={classNames(className, css.fullArea)}
          onClick={this.props.onClick}
        >
          {mapUnavailableNotice}
          {this.currentMarkers.map(m => {
            // Remove existing activeLabel classes and add it only to the correct container
            m.markerContainer.classList.remove(css.activeLabel);
            if (m.componentProps?.isActive) {
              m.markerContainer.classList.add(css.activeLabel);
            }

            const isMapReadyForMarkers = this.map && m.markerContainer;
            // DOM node that should be used as portal's root
            const portalDOMContainer = isMapReadyForMarkers
              ? document.getElementById(m.markerContainer.id)
              : null;

            // Create component portals for correct marker containers
            if (isMapReadyForMarkers && m.type === 'price') {
              return ReactDOM.createPortal(
                <SearchMapPriceLabel
                  {...m.componentProps}
                  config={config}
                  selectedCurrency={selectedCurrency}
                  exchangeRates={exchangeRates}
                />,
                portalDOMContainer
              );
            } else if (isMapReadyForMarkers && m.type === 'group') {
              return ReactDOM.createPortal(
                <SearchMapGroupLabel {...m.componentProps} />,
                portalDOMContainer
              );
            }
            return null;
          })}
          {this.state.mapContainer && this.currentInfoCard
            ? ReactDOM.createPortal(
              <SearchMapInfoCard
                {...this.currentInfoCard.componentProps}
                config={config}
                selectedCurrency={selectedCurrency}
                exchangeRates={exchangeRates}
              />,
              this.currentInfoCard.markerContainer
            )
            : null}
        </div>
      </>
    );
  }
}

SearchMapWithMapbox.defaultProps = {
  id: 'map',
  center: null,
  priceLabels: [],
  infoCard: null,
  zoom: 11,
  reusableMapHiddenHandle: null,
  clickedListings: null,
  onMapUnavailable: null,
};

SearchMapWithMapbox.propTypes = {
  id: string,
  center: propTypes.latlng,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  priceLabels: arrayOf(node),
  infoCard: node,
  onClick: func.isRequired,
  onMapMoveEnd: func.isRequired,
  onMapLoad: func.isRequired,
  // Called once when the browser cannot run Mapbox GL; the parent may collapse the map area.
  onMapUnavailable: func,
  zoom: number,
  reusableMapHiddenHandle: string,
  config: object.isRequired,
  onToggleFullscreen: func,
  isFullscreen: bool,
  onClose: func,
  clickedListings: arrayOf(propTypes.listing),
};

export default SearchMapWithMapbox;